import React, { useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Chip,
  Typography,
  List,
  ListItem,
  Tooltip,
  IconButton,
  ListItemIcon,
  Alert,
  Button,
} from '@mui/material'
import {
  PlaylistRemove as PlaylistRemoveIcon,
  DriveFileMove as MoveIcon,
  FormatQuote as FormatQuoteIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import type { KeyPhrasesViewProps } from './Types'
import { useCommonStyles, useKeyPhrasesStyles } from '../Hooks/UseThemeStyles'
import { truncateText } from 'containers/TextAnalysis/Helpers/ThemesHelper'

const KeyPhrasesView: React.FC<KeyPhrasesViewProps> = ({
  keyPhrases,
  onMoveClick,
  onDeleteClick,
  confirmationPhrase,
  onConfirmationChange,
}) => {
  const { t } = useTranslation()
  const styles = useKeyPhrasesStyles()
  const commonStyles = useCommonStyles()

  const alertRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (confirmationPhrase && alertRef.current) {
      alertRef.current.focus()
    }
  }, [confirmationPhrase])

  const sortedKeyPhrases = useMemo(
    () => [...keyPhrases].sort((a, b) => a.localeCompare(b)),
    [keyPhrases],
  )

  const handleDeleteClick = (phrase: string) => {
    onConfirmationChange?.(phrase)
  }

  const handleConfirmDelete = () => {
    if (confirmationPhrase) {
      onDeleteClick?.(confirmationPhrase)
      onConfirmationChange?.(null)
    }
  }

  const handleCancelDelete = () => {
    onConfirmationChange?.(null)
  }

  return (
    <>
      {confirmationPhrase && (
        <Alert
          severity="warning"
          sx={styles.alert}
          action={
            <Box sx={styles.alertActions}>
              <Button color="inherit" size="small" onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button color="error" size="small" onClick={handleConfirmDelete}>
                Delete
              </Button>
            </Box>
          }
          aria-live="assertive"
          ref={alertRef}
          tabIndex={-1}
        >
          Are you sure you want to delete the phrase <br />
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            {truncateText(confirmationPhrase, 45)} ?
          </Typography>
        </Alert>
      )}

      <Box sx={styles.headerBox}>
        <Box sx={styles.titleBox}>
          <Typography variant="button" color="text.secondary" fontSize={12}>
            {t('textAnalysis.aiThemes.keyPhrases')}
          </Typography>
          <Chip label={keyPhrases.length} size="small" sx={styles.chip} />
        </Box>
        {keyPhrases.length > 0 && (
          <Typography variant="button" color="text.secondary" fontSize={12}>
            {t('textAnalysis.aiThemes.actions')}
          </Typography>
        )}
      </Box>

      {keyPhrases.length > 0 ? (
        <List sx={styles.list}>
          {sortedKeyPhrases.map((phrase: string, index: number) => {
            const isLong = phrase.length > 60
            const isPendingDelete = confirmationPhrase === phrase

            return (
              <ListItem
                key={`${phrase}-${index}`}
                sx={{
                  ...styles.listItem,
                  ...(isPendingDelete && styles.pendingDeleteItem),
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Move key phrase" placement="left-end">
                      <IconButton
                        aria-label="Move key phrase"
                        onClick={(e) => {
                          e.stopPropagation()
                          onMoveClick?.(phrase)
                        }}
                      >
                        <MoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete key phrase" placement="right-end">
                      <IconButton
                        aria-label="Delete key phrase"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick?.(phrase)
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    overflow: 'hidden',
                    pr: '3rem',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                    <FormatQuoteIcon sx={styles.quoteIcon} />
                  </ListItemIcon>
                  <Tooltip
                    title={isLong ? phrase : ''}
                    placement="top"
                    followCursor
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: '100%',
                        ...(isLong ? commonStyles.longText : {}),
                      }}
                    >
                      {phrase}
                    </Typography>
                  </Tooltip>
                </Box>
              </ListItem>
            )
          })}
        </List>
      ) : (
        <Box sx={styles.emptyBox}>
          <PlaylistRemoveIcon sx={styles.emptyIcon} />
          <Typography variant="body2" color="text.secondary">
            No key phrases found
          </Typography>
        </Box>
      )}
    </>
  )
}

export default React.memo(KeyPhrasesView)
